// @flow

// Libs
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { GeoJSON, Popup, Circle } from 'react-leaflet'
import _get from 'lodash/get'
import _round from 'lodash/round'

// utils
import traverse from 'fc-utils/traverse'
import convert from 'convert-units'

// Types
import type {
  Asset,
} from 'fc-model-assets/types'

// Messages for translations
import messages from '../messages'

// Styles
import { primary as primaryFC } from 'fc-styles/src/colors'
import { primary, white } from 'hfty-styles/src/colors'
import { GRID_UNIT_ACRES } from '../../../../models/hfty-model-grid/src/constants'

// Props
type Props = {
  field: Asset,
  farm: Asset,
  selected: boolean,
  selectedFeatureIndex: ?number,
  hoverFeatureIndex: ?number,
}

function getFeatureIndexProp (feature: Object): number {
  const { properties } = feature
  let featureIndex

  if (typeof properties['FID'] === 'number') {
    featureIndex = properties['FID']
  } else {
    featureIndex = feature.properties['featureIndex']
  }

  return featureIndex
}

// Exports
class HeftyField extends Component {
  props: Props
  _shapeUpdates: number

  constructor () {
    super()

    this._shapeUpdates = 0

    this.state = {
      latlng: [],
    }

    this.onHighlight = (props: Object): Function =>
      (feature: Object, layer: Object): Function =>
        this.onEachFeature(props: Object, feature, layer)
  }

  componentWillMount () {
    this._FarmMessage = this.context.intl.formatMessage({...messages['heftyMap.farm']})
    this._FieldMessage = this.context.intl.formatMessage({...messages['heftyMap.field']})
    this._AreaMessage = this.context.intl.formatMessage({...messages['heftyMap.area']})
    this._acres = this.context.intl.formatMessage({...messages['heftyMap.acres']})
    this._hectares = this.context.intl.formatMessage({...messages['heftyMap.hectares']})
  }

  componentWillReceiveProps (nextProps: Props) {
    if (this.props.field !== nextProps.field) {
      this._shapeUpdates++
    }
  }

  onEachFeature (props: Object, feature: Object, layer: Object) {
    const { selected, onSelectFeature, onToggleHoverFeature } = props

    if (selected) {
      const featureIndex = getFeatureIndexProp(feature)

      layer.addEventListener('mouseover', () => { onToggleHoverFeature(featureIndex) })
      layer.addEventListener('mouseout', () => { onToggleHoverFeature(null) })
      layer.addEventListener('click', () => { onSelectFeature(featureIndex) })
    }
  }

  renderFieldPopup (): React.Element<any> {
    const { farm, field } = this.props
    const unit = _get(field, 'shape.shapeData.units', GRID_UNIT_ACRES)
    const unitAbrv = unit !== GRID_UNIT_ACRES ? 'ha' : 'ac'
    const area = _round(traverse(['shape', 'shapeData', 'area'])(field), 2)
    const convArea = unit === GRID_UNIT_ACRES ? area : convert(area).from('ac').to(unitAbrv).toFixed(2)
    const transUnit = unit === GRID_UNIT_ACRES ? this._acres : this._hectares

    return (
      <Popup
        onClose={(): void => this.setState({latlng: []})}
      >
        <div>
          <div>{`${this._FieldMessage}: ${field.label}`}</div>
          <div>{`${this._FarmMessage}: ${farm.label}`}</div>
          <div>
            {`${this._AreaMessage}: ${convArea} ${transUnit}`}
          </div>
        </div>
      </Popup>)
  }

  renderCentroid (): React.Element<any> {
    const { field } = this.props

    return (
      <Circle
        center={traverse(['shape', 'shapeData', 'centroid'])(field)}
        radius={4}
        color={'Magenta'}
      />
    )
  }

  render (): React.Element<any> {
    const { field, selected } = this.props

    return (
      <div data-test='hefty-field' key={this._shapeUpdates}>
        <GeoJSON
          key={selected}
          data={field.shape.shapeData}
          onEachFeature={this.onHighlight(this.props)}
          onClick={(e: Event): void => this.setState({latlng: e.latlng})}
          style={(feature: Object): Object => {
            let style = {
              color: primaryFC(),
              fillOpacity: 0.2,
            }

            if (this.props.selected) {
              const featureIndex = getFeatureIndexProp(feature)
              if (featureIndex === this.props.hoverFeatureIndex) {
                style.fillOpacity = 0.8
              }

              if (featureIndex === this.props.selectedFeatureIndex) {
                style.color = primary()
              } else if (this.props.selected) {
                style.color = white()
              }
            }

            return style
          }}
        >
          {this.renderFieldPopup()}
        </GeoJSON>
        {this.renderCentroid()}
      </div>
    )
  }
}

HeftyField.contextTypes = {
  intl: PropTypes.object.isRequired,
}

export default HeftyField
